import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface App {
  id: string
  name: string
  slug: string
  developer: string
  category: string
  description: string
  version: string
  icon_url: string
  rating: number
  reviews_count: number
  installs: number
  pricing: string
  price_monthly?: number
  status: string
  features: string[]
  permissions: string[]
}

interface Installation {
  id: string
  app_id: string
  customer_name: string
  status: string
  installed_at: string
}

export default function MarketplacePage() {
  const [apps, setApps] = useState<App[]>([])
  const [installations, setInstallations] = useState<Installation[]>([])
  const [categories, setCategories] = useState<any[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedApp, setSelectedApp] = useState<App | null>(null)
  const [filterCategory, setFilterCategory] = useState<string | null>(null)

  useEffect(() => {
    fetchData()
  }, [filterCategory])

  const fetchData = async () => {
    const categoryParam = filterCategory ? `?category=${filterCategory}` : ''
    const [appRes, instRes, catRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/marketplace/apps' + categoryParam),
      fetch(API_BASE + '/marketplace/installations'),
      fetch(API_BASE + '/marketplace/categories'),
      fetch(API_BASE + '/marketplace/statistics')
    ])
    if (appRes.ok) setApps((await appRes.json()).apps || [])
    if (instRes.ok) setInstallations((await instRes.json()).installations || [])
    if (catRes.ok) setCategories((await catRes.json()).categories || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': case 'active': return 'bg-green-100 text-green-800'
      case 'pending_review': return 'bg-yellow-100 text-yellow-800'
      case 'suspended': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const renderStars = (rating: number) => {
    const stars = []
    for (let i = 0; i < 5; i++) {
      stars.push(
        <span key={i} className={i < Math.floor(rating) ? 'text-yellow-400' : 'text-gray-300'}>★</span>
      )
    }
    return stars
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">App Marketplace</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Apps</div>
              <div className="text-2xl font-bold">{statistics.total_apps}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Published</div>
              <div className="text-2xl font-bold text-green-600">{statistics.published_apps}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Installs</div>
              <div className="text-2xl font-bold">{statistics.total_installs.toLocaleString()}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.active_installations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Avg Rating</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.average_rating}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Categories</div>
              <div className="text-2xl font-bold">{statistics.categories}</div>
            </div>
          </div>
        )}

        <div className="flex gap-4 mb-6">
          <button onClick={() => setFilterCategory(null)} className={`px-4 py-2 rounded-lg ${!filterCategory ? 'bg-blue-600 text-white' : 'bg-white border'}`}>All</button>
          {categories.map(cat => (
            <button key={cat.name} onClick={() => setFilterCategory(cat.name)} className={`px-4 py-2 rounded-lg ${filterCategory === cat.name ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
              {cat.name} ({cat.count})
            </button>
          ))}
        </div>

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2">
            <div className="grid grid-cols-2 gap-4">
              {apps.map(app => (
                <div key={app.id} className="bg-white rounded-lg shadow p-4 hover:shadow-lg cursor-pointer" onClick={() => setSelectedApp(app)}>
                  <div className="flex gap-4 mb-3">
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-2xl">📦</div>
                    <div className="flex-1">
                      <div className="font-semibold">{app.name}</div>
                      <div className="text-sm text-gray-500">{app.developer}</div>
                      <div className="flex items-center gap-1 mt-1">
                        {renderStars(app.rating)}
                        <span className="text-sm text-gray-500 ml-1">({app.reviews_count})</span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{app.description}</p>
                  <div className="flex justify-between items-center">
                    <span className={`px-2 py-1 rounded text-xs ${app.pricing === 'free' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                      {app.pricing === 'free' ? 'Free' : app.pricing === 'freemium' ? 'Freemium' : `$${app.price_monthly}/mo`}
                    </span>
                    <span className="text-sm text-gray-500">{app.installs.toLocaleString()} installs</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-6">
            {selectedApp ? (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex gap-4 mb-4">
                  <div className="w-20 h-20 bg-gray-100 rounded-lg flex items-center justify-center text-3xl">📦</div>
                  <div>
                    <h3 className="text-lg font-semibold">{selectedApp.name}</h3>
                    <div className="text-sm text-gray-500">{selectedApp.developer}</div>
                    <div className="text-sm text-gray-500">v{selectedApp.version}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  {renderStars(selectedApp.rating)}
                  <span className="text-sm">{selectedApp.rating} ({selectedApp.reviews_count} reviews)</span>
                </div>

                <p className="text-sm text-gray-600 mb-4">{selectedApp.description}</p>

                <div className="mb-4">
                  <div className="text-sm font-medium mb-2">Features</div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {selectedApp.features.map((f, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <span className="text-green-500">✓</span>{f}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mb-4">
                  <div className="text-sm font-medium mb-2">Permissions Required</div>
                  <div className="flex flex-wrap gap-2">
                    {selectedApp.permissions.map((p, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">{p}</span>
                    ))}
                  </div>
                </div>

                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {selectedApp.pricing === 'free' ? 'Install App' : `Install - $${selectedApp.price_monthly}/mo`}
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">App Details</h3>
                <p className="text-gray-500 text-sm">Select an app to view details</p>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Recent Installations</h3>
              <div className="space-y-3">
                {installations.slice(0, 5).map(inst => (
                  <div key={inst.id} className="p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-sm">{apps.find(a => a.id === inst.app_id)?.name || inst.app_id}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${getStatusColor(inst.status)}`}>{inst.status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{inst.customer_name}</div>
                    <div className="text-xs text-gray-400">{new Date(inst.installed_at).toLocaleDateString()}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
